<script setup lang="ts">
  import { ref, onUnmounted, computed, type Ref } from 'vue'
  import _fetch from '@/utils/fetch'
  import MessageServiceCn from '@/components/AppMessageCn/messageCn'
  import { useUserStore } from '@common/stores/userStore'
  import { useTicketStore } from '@common/stores/userTicket'
  import Utf8 from 'crypto-js/enc-utf8'
  import AES from 'crypto-js/aes'
  import ECB from 'crypto-js/mode-ecb'
  import Pkcs7 from 'crypto-js/pad-pkcs7'
  import commoneEmitter, { EventCommonBusKey } from '@common/utils/eventBusMitt'
  import { eventBus } from '@common/utils/eventBus'
  import { useIsMobile } from '@common/hooks/useIsMobile'
  import BindPhoneService from '@common/components/AppEduBindPhone/eduBindPhone'
  import trackEvent from '@common/track/trackEvent'
  const isMobile = useIsMobile()

  // 声明 WxLogin 全局类型
  declare global {
    interface Window {
      WxLogin: new (config: {
        id: string
        appid: string
        scope: string
        redirect_uri: string
        state: string
        style?: string
        stylelite?: number
        href?: string
        self_redirect?: boolean
        onReady?: (isReady: boolean) => void
      }) => void
      _ENV_: string
      isLoggedIn: boolean
      // dataLayer?: any[];
      // gtag?: (...args: any[]) => void;
    }
  }
  const polling = ref(false) // 轮询状态
  const wxLoginTimer = ref<ReturnType<typeof setInterval> | null>(null)

  const props = defineProps({
    type: {
      type: String,
      required: false,
      default: 'loginCn'
    },
    canClose: {
      type: Boolean,
      default: true
    }
  })

  const emit = defineEmits(['close', 'ok'])
  const loginDialogType = ref('phoneLogin')
  // 显示隐藏
  const showResetInput = ref(false)
  const showResetContent = ref(false)
  const modifySuccess = ref(false)
  const QRCodeVisible = ref(false)

  const forgetPSDVisible = ref(false)
  // const wxTitleVisible = ref(true);

  const loading = ref(false)
  // 常量值
  const passwordReg = /^(?=.*[a-zA-Z])(?=.*\d)[A-Za-z\d\W_]{6,20}$/ // 6-20位数字、字母组合
  const phoneReg = /^1[3456789]\d{9}$/ // 手机号正则
  const emailReg = /^[\w-]+(\.[\w-]+)*@[\w-]+(\.[\w-]+)+$/ // 邮箱正则
  // 更新邮箱正则
  // const emailReg = /^[a-zA-Z0-9_.-]+@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.[a-zA-Z0-9]{2,6}$/;
  // 更严格的版本（如果需要）
  // const emailReg = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
  const phoneCodeReg = /^\d{6}$/ // 手机验证码正则
  const emailCodeReg = /^\d{6}$/ // 邮箱验证码正则
  const publicKey = '***_shda***_shda'
  // 数据
  const phoneNumber = ref('')
  const phoneCode = ref('')

  const emailNumber = ref('')
  const emailPassword = ref('')

  const inputResetContent = ref('')
  const phoneOrEmailCode = ref('')
  const firstPassword = ref('')
  const confirmPassword = ref('')

  let signing = ref(false)

  const ticketStore = useTicketStore()
  const userStore = useUserStore()
  const isEduAccount = ref(false) // 是否为教育账号
  const validateData = ref()
  /**
   * 解析微信登录URL参数
   * @param url 完整的微信登录URL
   * @returns 解析后的参数对象
   */
  const parseWxLoginUrl = (url: string): WxLoginParams | null => {
    try {
      // 1. 分割基础URL和参数
      const [baseUrl, paramsStr] = url.split('?')

      // 2. 解析参数
      const params = new URLSearchParams(paramsStr)

      // 3. 返回格式化的对象
      return {
        baseUrl,
        params: {
          response_type: params.get('response_type'), // 返回类型
          appid: params.get('appid'), // 应用ID
          redirect_uri: decodeURIComponent(params.get('redirect_uri') || ''), // 回调地址
          scope: params.get('scope'), // 授权范围
          state: params.get('state') // 状态码
        }
      }
    } catch (error) {
      console.error('解析微信登录URL失败:', error)
      return null
    }
  }
  // 定义微信登录URL参数的接口
  interface WxLoginParams {
    baseUrl: string
    params: {
      response_type: string | null
      appid: string | null
      redirect_uri: string
      scope: string | null
      state: string | null
    }
  }
  // 在需要使用时加载
  const initWXLogin = async () => {
    try {
      await loadWXScript()

      const response = await _fetch('/aigc/api/auth/login/pre/third', {
        method: 'GET',
        params: { identifierType: 3 }
      })
      const res = await response.json()
      if (res.code === 200 && res.data?.url) {
        const result = parseWxLoginUrl(res.data.url)
        if (!result || !result.params.appid) {
          throw new Error('无效的微信登录参数')
        }
        new window.WxLogin({
          id: 'wx_login_container',
          appid: result.params.appid || '',
          scope: 'snsapi_login',
          redirect_uri: encodeURIComponent(result.params.redirect_uri) || '',
          state: result.params.state || '',
          style: 'black',
          // stylelite: 1,
          // href: `data:text/css;base64,${btoa(customStyle)}`,
          self_redirect: true
          // onReady: (isReady: boolean) => {
          //     console.log('微信登录初始化状态:', isReady);
          // }
        })
        startPolling(res.data.state)
      }
    } catch (error) {
      console.error('微信登录初始化失败:', error)
      MessageServiceCn({
        message: '初始化失败,请重试',
        type: 'error'
      })
    }
  }
  // 轮询检查登录状态
  const startPolling = async (state: string) => {
    if (polling.value) return

    polling.value = true
    wxLoginTimer.value = setInterval(async () => {
      try {
        const response = await _fetch('/aigc/api/auth/login/query/third', {
          method: 'get',
          params: { uuid: state }
        })
        const res = await response.json()
        if (res.code === 200) {
          if (res.data.requiredBinding == 'mobile') {
            const validateAuthToken = res.data.validateAuthToken
            emit('close')
            BindPhoneService({
              validateAuthToken,
              onSuccess(data: any) {
                loginCallback({ data: data })
                eventBus.trigger('refreshHeader')
                clearPolling()
                if (data.eduIdentity === 'student') {
                  commoneEmitter.emit(EventCommonBusKey.showEduWelcomModal)
                }
              }
            })
          } else {
            clearPolling()
            loginCallback(res)
          }
        }
      } catch (error) {
        console.error('检查登录状态失败:', error)
        clearPolling()
      }
    }, 500)

    setTimeout(() => {
      clearPolling()
    }, 60000)
  }
  // 清理轮询
  const clearPolling = () => {
    if (wxLoginTimer.value) {
      clearInterval(wxLoginTimer.value as unknown as number)
      wxLoginTimer.value = null
    }
    polling.value = false
  }

  // 点击微信图标时调用
  const handleWXLogin = () => {
    QRCodeVisible.value = true
    trackEvent.track('login_click', {
      login_method: 'wechat'
    })
    initWXLogin()
  }
  // 组件卸载时清理
  onUnmounted(() => {
    clearPolling()
  })
  // 动态加载微信登录 SDK
  const loadWXScript = () => {
    return new Promise((resolve, reject) => {
      if (window.WxLogin) {
        resolve(window.WxLogin)
        return
      }
      const script = document.createElement('script')
      script.type = 'text/javascript'
      script.src = 'https://res.wx.qq.com/connect/zh_CN/htmledition/js/wxLogin.js'
      script.onload = () => resolve(window.WxLogin)
      script.onerror = reject
      document.head.appendChild(script)
    })
  }

  // #region 验证码相关
  // 手机验证码状态
  const phoneCodeStatus = ref(false)
  const phoneCodeTime = ref(60)
  let phoneCodeTimer: ReturnType<typeof setInterval> | null = null

  // 邮箱验证码状态
  const emailCodeStatus = ref(false)
  const emailCodeTime = ref(60)
  let emailCodeTimer: ReturnType<typeof setInterval> | null = null

  // 发送手机验证码
  const sendPhoneCode = async () => {
    //const { type, content, url, codeStatus, codeTime, codeTimer } = params;
    // 发送中或倒计时中禁止重复发送
    if (phoneCodeStatus.value) return
    // 根据类型验证格式
    const isValid = phoneReg.test(phoneNumber.value)

    if (!isValid) {
      MessageServiceCn({
        message: '手机号格式错误，请重新输入',
        type: 'error'
      })
      return
    }
    phoneCodeStatus.value = true
    const response = await _fetch('/aigc/api/auth/login/mobile/code', {
      method: 'POST',
      params: {
        mobile: phoneNumber.value
      },
      body: JSON.stringify({
        mobile: phoneNumber.value
      })
    })
    const res = await response.json()
    if (res.code !== 200) {
      MessageServiceCn({
        message: res.msg,
        type: 'error'
      })
    }
    startCountDown(phoneCodeStatus, phoneCodeTime, phoneCodeTimer)
  }

  // 发送邮箱验证码
  const sendEmailCode = async () => {
    // 发送中或倒计时中禁止重复发送
    if (emailCodeStatus.value) return
    emailCodeStatus.value = true
    const response = await _fetch('/aigc/api/auth/forgot/password/code', {
      method: 'POST',
      params: {
        validateType: validateData.value?.validateType,
        validateAuthToken: validateData.value?.validateAuthToken
      },
      body: JSON.stringify({
        validateType: validateData.value?.validateType,
        validateAuthToken: validateData.value?.validateAuthToken
      })
    })
    const res = await response.json()
    if (res.code !== 200) {
      MessageServiceCn({
        message: res.msg,
        type: 'error'
      })
    }
    startCountDown(emailCodeStatus, emailCodeTime, emailCodeTimer)
  }
  // 验证码发送接口参数类型
  interface CodeParams {
    type: 'mobile' | 'email'
    content: string
    url: string
    codeStatus: Ref<boolean>
    codeTime: Ref<number>
    codeTimer: ReturnType<typeof setInterval> | null
  }
  // 统一的发送验证码方法
  const sendCode = async (params: CodeParams) => {
    const { type, content, url, codeStatus, codeTime, codeTimer } = params

    // 发送中或倒计时中禁止重复发送
    if (codeStatus.value) return
    // 根据类型验证格式
    const isValid = type === 'mobile' ? phoneReg.test(content) : emailReg.test(content)

    if (!isValid) {
      MessageServiceCn({
        message: `${type === 'mobile' ? '手机号' : '邮箱'}格式错误，请重新输入`,
        type: 'error'
      })
      return
    }

    try {
      codeStatus.value = true
      // 调用验证码接口
      await _fetch(url, {
        method: 'GET',
        params: {
          [type]: content,
          tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
          type: type === 'email' ? 'FORGET_PASSWORD' : ''
        }
      })
      // 开始倒计时
      startCountDown(codeStatus, codeTime, codeTimer)
    } catch (error) {
      codeStatus.value = false
      MessageServiceCn({
        message: '发送失败，请重试',
        type: 'error'
      })
    }
  }

  // 统一的倒计时方法
  const startCountDown = (
    codeStatus: Ref<boolean>,
    codeTime: Ref<number>,
    codeTimer: ReturnType<typeof setInterval> | null
  ) => {
    // 清理已有定时器
    if (codeTimer) {
      clearInterval(codeTimer)
    }

    codeTimer = setInterval(() => {
      if (codeTime.value > 0) {
        codeTime.value--
      } else {
        // 倒计时结束
        codeStatus.value = false
        codeTime.value = 60
        clearInterval(codeTimer!)
        codeTimer = null
      }
    }, 1000)
  }

  // 组件卸载时清理定时器
  onUnmounted(() => {
    if (phoneCodeTimer) clearInterval(phoneCodeTimer)
    if (emailCodeTimer) clearInterval(emailCodeTimer)
  })

  /**
   * AES加密
   */
  function encryptAes(data: string): string {
    const aesKey = publicKey
    if (aesKey && data) {
      const key = Utf8.parse(aesKey)
      const result = AES.encrypt(data, key, {
        mode: ECB,
        padding: Pkcs7
      }).toString()
      return result.replace(/\+/g, '{cand}')
    } else {
      return data
    }
  }
  const emailLogin = async () => {
    if (signing.value) return
    trackEvent.track('login_click', {
      login_method: 'email'
    })
    // 登录不校验账号规则
    if (emailPassword.value === '') {
      // if (!emailReg.test(emailNumber.value) || emailPassword.value === '') {
      MessageServiceCn({
        message: '密码不能为空',
        type: 'error'
      })
      return
    }
    signing.value = true
    const response = await _fetch('/aigc/api/auth/login/account', {
      method: 'POST',
      body: JSON.stringify({
        account: emailNumber.value,
        password: encryptAes(emailPassword.value)
      })
    })
    signing.value = false
    const res = await response.json()
    if (res.code === 200) {
      if (res.data.requiredBinding == 'mobile') {
        const validateAuthToken = res.data.validateAuthToken
        emit('close')
        BindPhoneService({
          validateAuthToken,
          onSuccess(data: any) {
            loginCallback({ data: data })
            eventBus.trigger('refreshHeader')
            if (data.eduIdentity === 'student') {
              commoneEmitter.emit(EventCommonBusKey.showEduWelcomModal)
            }
          }
        })
      } else {
        loginCallback(res)
      }
    } else {
      MessageServiceCn({
        message: res.msg,
        type: 'error'
      })
    }
  }
  const loginCallback = (res: any) => {
    // 1. 立即更新登录状态
    window.isLoggedIn = true
    userStore.setUser(res.data)
    userStore.setIsLogin(true)
    ticketStore.setTicket(res.data.balance || '0')
    // 2. 触发路由跳转
    emit('ok', res.data)
    // TODO 后端没有返回ticket，所以临时获取下
    getTicketCount()
    eventBus.trigger('mountNotice')
    // 开屏弹窗未登录时，缓存数据上报
    messageRead()
    window.dataLayer &&
      window.dataLayer.push({
        user_name: res.data?.nickName || '',
        userId: res.data?.userId || '',
        from_link: sessionStorage.getItem('ht_source') || ''
      })
    window.gtag &&
      window.gtag('event', 'login_success', {
        user_name: res.data?.nickName || '',
        userId: res.data?.userId || '',
        from_link: sessionStorage.getItem('ht_source') || ''
      })

    // 火山埋点
    trackEvent.track('login_success', {
      login_method:
        loginDialogType.value === 'phoneLogin'
          ? 'sms'
          : loginDialogType.value === 'accountLogin'
            ? 'email'
            : 'wechat'
    })
    trackEvent.track('config', {
      user_unique_id: res.data?.userId || '',
      user_name: res.data?.nickName || ''
    })
  }
  const messageRead = async () => {
    const stored = localStorage.getItem('msgIds')
    const msgIds = stored ? JSON.parse(stored) : []
    if (msgIds.length > 0) {
      try {
        const response = await _fetch('/aigc/api/message/read', {
          method: 'PUT',
          params: {
            msgIdList: msgIds
          }
        })
        const res = await response.json()
        if (res.code === 200) {
          console.log('readNotice success')
          localStorage.setItem('msgIds', JSON.stringify([]))
        }
      } catch (error) {
        console.error('readNotice error:', error)
      }
    }
  }

  const getTicketCount = async () => {
    const response = await _fetch('/aigc/api/user/account')
    const res = await response.json()
    if (res.code === 200) {
      ticketStore.setTicket(res.data)
    }
  }

  const phoneLoginBtn = async () => {
    if (signing.value) return
    trackEvent.track('login_click', {
      login_method: 'sms'
    })
    if (!phoneReg.test(phoneNumber.value)) {
      // -------要跟产品确认下这里的手机号码格式和验证码格式是否需要前端校验-交互问题- TODO----
      MessageServiceCn({
        message: '手机号格式错误，请重新输入',
        type: 'error'
      })
      return
    }
    if (!phoneCodeReg.test(phoneCode.value)) {
      MessageServiceCn({
        message: '验证码格式错误，请重新输入',
        type: 'error'
      })
      return
    }
    signing.value = true
    const response = await _fetch('/aigc/api/auth/login/mobile', {
      method: 'POST',
      body: JSON.stringify({
        mobile: phoneNumber.value,
        // code: encryptAes(phoneCode.value),
        code: phoneCode.value
      })
    })
    signing.value = false
    const res = await response.json()
    if (res.code === 200) {
      loginCallback(res)
    } else {
      MessageServiceCn({
        message: res.msg || '登录失败',
        type: 'error'
      })
    }
  }

  // 密码显示隐藏
  const handlePassword = (event: Event) => {
    event.stopPropagation()
    const target = event.target as HTMLInputElement
    const pre = target.previousSibling as HTMLInputElement
    if (pre) {
      const type = pre.type
      if (type === 'password') {
        pre.type = 'text'
        target.className = 'password-icon close'
      } else {
        pre.type = 'password'
        target.className = 'password-icon'
      }
    }
  }
  const forgetPasswordBtn = () => {
    showResetInput.value = true
    showResetContent.value = false
    modifySuccess.value = false
    forgetPSDVisible.value = true // 忘记密码
  }

  const nextStep = async () => {
    if (inputResetContent.value === '') {
      MessageServiceCn({
        message: '请输入邮箱/账号',
        type: 'error'
      })
      return
    }
    // 校验邮箱是否存在
    const response = await _fetch('/aigc/api/auth/forgot/password/validate', {
      method: 'GET',
      params: {
        account: inputResetContent.value
      }
    })
    const res = await response.json()
    if (res.code === 200) {
      validateData.value = res.data
      showResetInput.value = false
      showResetContent.value = true
    } else {
      MessageServiceCn({
        message: res.msg || '服务错误',
        type: 'error'
      })
    }
  }

  const resetPassword = async () => {
    if (signing.value) return
    if (
      inputResetContent.value === '' ||
      phoneOrEmailCode.value === '' ||
      firstPassword.value === '' ||
      confirmPassword.value === ''
    ) {
      MessageServiceCn({
        message: '请填写验证码、密码和确认密码',
        type: 'error'
      })
      return
    }
    if (firstPassword.value !== confirmPassword.value) {
      MessageServiceCn({
        message: '两次密码不一致，请您重新确认',
        type: 'error'
      })
      return
    }
    if (!passwordReg.test(firstPassword.value)) {
      MessageServiceCn({
        message:
          '密码必须在6到20个字符之间，至少包含字母、数字和特殊字符的其中两种，并且不能包含空格',
        type: 'error'
      })
      return
    }
    signing.value = true
    const response = await _fetch('/aigc/api/auth/forgot/password/submit', {
      method: 'POST',
      params: {
        validateType: validateData.value?.validateType,
        validateAuthToken: validateData.value?.validateAuthToken
      },
      body: JSON.stringify({
        validateType: validateData.value?.validateType,
        validateAuthToken: validateData.value?.validateAuthToken,
        password: encryptAes(firstPassword.value),
        matchingPassword: encryptAes(confirmPassword.value),
        code: phoneOrEmailCode.value
      })
    })
    signing.value = false
    const res = await response.json()
    if (res.code === 200) {
      MessageServiceCn({
        message: res.msg,
        type: 'success'
      })
      modifySuccess.value = true
      forgetPSDVisible.value = false
    } else {
      MessageServiceCn({
        message: res.msg,
        type: 'error'
      })
    }
  }

  const backToLogin = () => {
    modifySuccess.value = false
    forgetPSDVisible.value = false
    showResetInput.value = false
    showResetContent.value = false
  }

  // 处理按钮状态
  const phoneSendBtnDisabled = computed(() => {
    return !phoneNumber.value?.trim() || phoneCodeStatus.value
  })
  const emailSendBtnDisabled = computed(() => {
    return !inputResetContent.value?.trim() || emailCodeStatus.value
  })
  const isAllPhoneInputsFilled = computed(() => {
    // return phoneNumber.value.trim() &&
    //        phoneCode.value.trim()
    return phoneReg.test(phoneNumber.value) && phoneCodeReg.test(phoneCode.value)
  })
  const isAllEmailInputsFilled = computed(() => {
    return (
      emailNumber.value?.trim() &&
      //        emailPassword.value?.trim()
      // return emailReg.test(emailNumber.value) &&
      emailPassword.value.trim()
    )
    //   passwordReg.test(emailPassword.value);
  })

  const isAllInputsFilled = computed(() => {
    // return inputResetContent.value.trim() &&
    //        phoneOrEmailCode.value.trim() &&
    //        firstPassword.value.trim() &&
    //        confirmPassword.value.trim();
    // return emailReg.test(inputResetContent.value) &&
    return (
      inputResetContent.value.trim() &&
      emailCodeReg.test(phoneOrEmailCode.value) &&
      passwordReg.test(firstPassword.value) &&
      passwordReg.test(confirmPassword.value)
    )
  })

  // 键盘事件处理函数
  const handleKeyup = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      const targetId = (e.target as HTMLInputElement).name
      switch (targetId) {
        case 'emailLogin':
          emailLogin()
          break
        //   case 'emailRegister':
        //     emailRegister()
        // break
        case 'phoneLogin':
          phoneLoginBtn()
          break
        case 'resetPassword':
          resetPassword()
          break
        default:
          break
      }
    }
  }
</script>
<template>
  <main class="login-mask-container" v-if="!isEduAccount" id="login_cn_container">
    <div v-if="!QRCodeVisible && !forgetPSDVisible" class="login-container" @click.stop="() => {}">
      <a v-if="props.canClose" class="detail-close" @click.stop="!loading && $emit('close')">
        <span data-v-1f531508="" class="hitems-close"></span>
      </a>
      <div v-if="!modifySuccess" class="login-header">
        <img class="login-logo" src="@/assets/images/logo-zhw.webp" height="32" alt="logo" />
      </div>
      <div class="login-header-tab" v-if="!showResetInput && !showResetContent && !modifySuccess">
        <span
          @click="loginDialogType = 'phoneLogin'"
          :class="loginDialogType === 'phoneLogin' ? 'active' : ''"
          style="cursor: pointer"
        >
          手机号登录
        </span>
        <span></span>
        <span
          @click="loginDialogType = 'accountLogin'"
          :class="loginDialogType === 'accountLogin' ? 'active' : ''"
          style="cursor: pointer"
        >
          账号登录
        </span>
      </div>
      <!-- 手机/邮箱登录框 -->
      <div v-if="!showResetInput && !showResetContent && !modifySuccess" style="width: 100%">
        <div class="login-types">
          <!-- 手机验证码登录 -->
          <div v-if="loginDialogType === 'phoneLogin'">
            <div class="login-btn">
              <input
                v-model="phoneNumber"
                @keyup="handleKeyup"
                name="phone"
                class="login-input"
                type="text"
                placeholder="输入手机号"
              />
            </div>
            <div class="login-btn">
              <input
                v-model="phoneCode"
                @keyup="handleKeyup"
                name="password"
                class="login-input"
                type="text"
                inputmode="numeric"
                autocomplete="one-time-code"
                pattern="\d{6}"
                placeholder="输入验证码"
              />
              <span
                :class="['send-code', { 'login-btn-disabled': phoneSendBtnDisabled }]"
                @click="sendPhoneCode()"
              >
                {{ phoneCodeStatus ? `重新发送(${phoneCodeTime}s)` : '发送验证码' }}
              </span>
            </div>
            <div
              @click="phoneLoginBtn"
              class="login-btn margin-special"
              :class="{
                'login-btn-sign': isAllPhoneInputsFilled,
                'signing': signing,
                'login-btn-disabled': !isAllPhoneInputsFilled
              }"
            >
              <a style="font-size: 18px; font-weight: 600; color: #fff">登录</a>
            </div>
          </div>
          <!-- 邮箱/账号密码登录 -->
          <div v-else>
            <div class="login-btn">
              <input
                v-model="emailNumber"
                @keyup="handleKeyup"
                name="email"
                class="login-input"
                type="text"
                placeholder="输入邮箱/账号"
              />
            </div>
            <div class="login-btn">
              <input
                v-model="emailPassword"
                @keyup="handleKeyup"
                name="password"
                class="login-input"
                type="password"
                placeholder="输入密码"
              />
              <a
                class="password-icon icon-with-text"
                style="margin-right: 0.4rem"
                @click="handlePassword"
              ></a>
              <span class="forget-password" @click="forgetPasswordBtn">忘记密码</span>
            </div>
            <div
              @click="emailLogin"
              class="login-btn margin-special"
              :class="{
                'login-btn-sign': isAllEmailInputsFilled,
                'signing': signing,
                'login-btn-disabled': !isAllEmailInputsFilled
              }"
            >
              <a style="font-size: 18px; font-weight: 600; color: #fff">登录</a>
            </div>
          </div>
          <div v-if="loginDialogType === 'phoneLogin'" class="login-tips">
            未注册的手机号将自动注册
          </div>
          <div v-else class="login-tips"></div>
          <div v-if="!isMobile" class="login-other">
            <span>其它登录方式</span>
          </div>
          <div v-else style="margin-bottom: 2.4rem; height: 2.4rem">
            <span></span>
          </div>
          <div v-if="!isMobile" class="other-login-type">
            <div class="other-login-type-item">
              <!-- 微信登录 -->
              <img @click="handleWXLogin" src="@/assets/images/wechat-logo.webp" alt="google" />
            </div>
          </div>
          <div v-else>
            <div
              class="other-login-type-item"
              style="margin-bottom: 2.4rem; width: 2.4rem; height: 2.4rem"
            >
              <!-- 微信登录 -->
              <img src="" alt="google" style="display: none" />
            </div>
          </div>
          <div class="login-bottom-tips-container" style="text-align: center">
            <div>
              登录即代表您已仔细阅读并同意
              <a
                href="https://zaohaowu.com/docs/%E5%B9%B3%E5%8F%B0%E7%94%A8%E6%88%B7%E5%8D%8F%E8%AE%AE"
                target="_blank"
                style="color: #6d5dff; margin: 0 -4px 0 -8px"
              >
                《服务条款》
              </a>
              和
              <a
                href="https://zaohaowu.com/docs/%E7%94%A8%E6%88%B7%E9%9A%90%E7%A7%81%E5%8D%8F%E8%AE%AE"
                target="_blank"
                style="color: #6d5dff; margin: 0 -4px 0 -8px"
              >
                《隐私政策》
              </a>
            </div>
          </div>
        </div>
      </div>
      <!-- 密码修改成功 -->
      <div v-if="modifySuccess" class="modify-success">
        <img class="done-logo" src="@/assets/images/done.webp" height="48" alt="logo" />
        <div class="tips">密码已修改成功，请返回</div>
        <div class="tips">
          重新
          <span style="color: #6d5dff" @click="backToLogin">登录</span>
        </div>
      </div>
    </div>

    <!--------------------------------- 忘记密码 ------------------------------------>
    <div v-if="forgetPSDVisible" class="login-container" @click.stop="() => {}">
      <a v-if="props.canClose" class="detail-close" @click.stop="!loading && $emit('close')">
        <span data-v-1f531508="" class="hitems-close"></span>
      </a>
      <div class="forget-title">忘记密码</div>
      <!-- 忘记密码输入框 -->
      <div v-if="showResetInput" class="reset-input forget-margin-special">
        <div class="login-types">
          <div class="login-btn forget-input">
            <input
              v-model="inputResetContent"
              @keyup="handleKeyup"
              class="login-input"
              type="text"
              placeholder="输入邮箱/账号"
            />
          </div>
          <div
            @click="nextStep"
            class="login-btn"
            :class="{
              'btn-active': inputResetContent.trim(),
              'btn-disabled': !inputResetContent.trim()
            }"
          >
            下一步
          </div>
        </div>
      </div>
      <!-- 重置密码输入框 -->
      <div v-if="showResetContent" class="reset-content">
        <div class="login-types">
          <div v-if="validateData.validateType !== 'mobile'" class="login-btn">
            <input
              v-model="inputResetContent"
              @keyup="handleKeyup"
              class="login-input"
              type="text"
              placeholder="输入邮箱/账号"
              disabled
            />
          </div>
          <div v-if="validateData.validateType == 'mobile'" class="login-btn">
            <input
              class="login-input"
              type="text"
              disabled
              :value="validateData.validateTypeAccount"
            />
          </div>
          <div class="login-btn">
            <input v-model="phoneOrEmailCode" class="login-input" placeholder="输入验证码" />
            <span
              :class="['send-code', { 'login-btn-disabled': emailSendBtnDisabled }]"
              @click="sendEmailCode()"
            >
              {{ emailCodeStatus ? `重新发送(${emailCodeTime}s)` : '发送验证码' }}
            </span>
          </div>
          <div class="login-btn">
            <input
              v-model="firstPassword"
              @keyup="handleKeyup"
              name="firstPassword"
              class="login-input"
              type="password"
              placeholder="输入新密码"
            />
            <a class="password-icon" @click="handlePassword"></a>
          </div>
          <div class="login-btn">
            <input
              v-model="confirmPassword"
              @keyup="handleKeyup"
              name="confirmPassword"
              class="login-input"
              type="password"
              placeholder="再次输入新密码"
            />
            <a class="password-icon" @click="handlePassword"></a>
          </div>
          <div
            @click="resetPassword"
            class="login-btn"
            :class="{ 'btn-active': isAllInputsFilled, 'btn-disabled': !isAllInputsFilled }"
          >
            确认
          </div>
        </div>
      </div>
    </div>

    <!-- 微信扫码登录 -->
    <div v-if="QRCodeVisible" class="qrcode-container">
      <!-- 微信二维码容器 -->
      <div id="wx_login_container" style="width: 300px; height: 400px; overflow: hidden"></div>
      <!-- 添加一个遮罩层 -->
      <div v-if="polling" class="wx-loading-mask"></div>
    </div>
  </main>
</template>
<style lang="stylus" scoped>
  @import './AppLoginCn.styl'
</style>
